'use client';

import { forwardRef, useState, type ReactNode, type HTMLAttributes } from 'react';
import { motion, AnimatePresence, type MotionProps } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CornerBrackets } from './CornerBrackets';
import { FiligreeDivider } from './FiligreeDivider';

interface GamePanelProps {
  title?: string;
  trailing?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  headerClassName?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  draggable?: boolean;
  tone?: 'default' | 'dark' | 'gilded';
  headerFiligree?: boolean;
}

const TONE_CLASSES: Record<NonNullable<GamePanelProps['tone']>, string> = {
  default:
    'bg-gradient-to-b from-zinc-950/75 to-black/70 border-amber-500/25',
  dark:
    'bg-gradient-to-b from-[#090608]/90 to-[#120508]/85 border-red-500/30',
  gilded:
    'bg-gradient-to-b from-[#1b1509]/80 to-[#0e0a05]/80 border-amber-400/40',
};

export const GamePanel = forwardRef<HTMLDivElement, GamePanelProps>(function GamePanel(
  {
    title,
    trailing,
    children,
    className,
    bodyClassName,
    headerClassName,
    collapsible = false,
    defaultCollapsed = false,
    draggable = false,
    tone = 'default',
    headerFiligree = true,
  },
  ref,
) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  const motionProps: MotionProps = draggable
    ? {
        drag: true,
        dragMomentum: false,
        dragElastic: 0.06,
        dragConstraints: { top: -200, left: -600, right: 600, bottom: 200 },
      }
    : {};

  return (
    <motion.div
      ref={ref}
      {...motionProps}
      // `data-hud-card` opts this shell into the bright-skybox contrast
      // boost handled in `lobby.module.css` — every GamePanel (BoardList,
      // RewardsPanel, HoldersPanel, OwnedTilesBar, etc.) inherits a near-
      // opaque body on bright skyboxes (Open Sky / Homestead) so copy stays readable
      // on outdoor fills. The default nocturnes keep their legacy
      // translucent treatment when `data-skybox-bright` is false (night / void hubs).
      data-hud-card
      className={cn(
        'relative rounded-md border backdrop-blur-md',
        'shadow-[inset_0_0_0_1px_rgba(255,215,120,0.06),0_20px_50px_-20px_rgba(0,0,0,0.9)]',
        TONE_CLASSES[tone],
        className,
      )}
    >
      <CornerBrackets />

      {title !== undefined && (
        <div
          className={cn(
            'relative flex items-center gap-2 px-3 py-2 border-b border-amber-500/15',
            'bg-gradient-to-b from-white/[0.04] to-transparent',
            draggable && 'cursor-grab active:cursor-grabbing select-none',
            headerClassName,
          )}
        >
          {headerFiligree && (
            <FiligreeDivider direction="left" width={28} className="opacity-70" />
          )}
          <span className="text-[11px] tracking-[0.18em] uppercase font-semibold text-amber-100/90 font-mono">
            {title}
          </span>
          {headerFiligree && (
            <FiligreeDivider direction="right" width={28} className="opacity-70" />
          )}
          <div className="ml-auto flex items-center gap-2">
            {trailing}
            {collapsible && (
              <button
                type="button"
                onClick={() => setCollapsed((v) => !v)}
                className="p-0.5 rounded hover:bg-white/5 text-amber-200/70 hover:text-amber-100 transition-colors"
                aria-label={collapsed ? 'Expand' : 'Collapse'}
              >
                <ChevronDown
                  className={cn(
                    'h-3.5 w-3.5 transition-transform duration-200',
                    collapsed && '-rotate-90',
                  )}
                />
              </button>
            )}
          </div>
        </div>
      )}

      {/* When collapsible, animate the body's height so the chevron toggle
          feels live. When not collapsible (e.g. the docked chat sidebar)
          we skip the height animation entirely — both because there's no
          chevron to drive it, and because animating `height: auto` caps
          the body to its content size, which would prevent flex-fill
          children (like the chat scroll list) from stretching to fill a
          full-height parent. */}
      {collapsible ? (
        <AnimatePresence initial={false}>
          {!collapsed && (
            <motion.div
              key="body"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
              className="overflow-hidden"
            >
              <div className={cn('p-3', bodyClassName)}>{children}</div>
            </motion.div>
          )}
        </AnimatePresence>
      ) : (
        <div className={cn('p-3', bodyClassName)}>{children}</div>
      )}
    </motion.div>
  );
});
